import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, Clock, UserCheck, CalendarPlus, ListChecks, CheckCheck } from "lucide-react";

const POLL_MS = 30000;

// icona per tipo evento
const typeIcon = (t) => {
  if (t === "waitlist_request") return ListChecks;
  if (t === "portal_login") return UserCheck;
  if (t === "portal_booking") return CalendarPlus;
  return Bell;
};

// tempo relativo in italiano (leggero, senza dipendenze)
const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ora";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const g = Math.floor(h / 24);
  return `${g} g fa`;
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=30");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // silenzioso: la campanella non deve bloccare l'app
      if (e?.response?.status && e.response.status !== 401) {
        console.debug("notifiche non disponibili", e?.response?.status);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => timer.current && clearInterval(timer.current);
  }, [load]);

  const destFor = (n) => {
    // Costruisce la destinazione dai dati della notifica: funziona anche per le
    // notifiche vecchie che avevano URL generici ("/clienti", "/agenda").
    const m = n.meta || {};
    if (n.type === "portal_booking" || (n.url && n.url.startsWith("/agenda"))) {
      const apptId = m.appointment_id || (n.url && n.url.match(/appt=([^&]+)/)?.[1]);
      const start = m.start || (n.url && n.url.match(/date=([^&]+)/)?.[1]);
      if (apptId) return `/agenda?date=${String(start || "").slice(0, 10)}&appt=${apptId}`;
      return "/agenda";
    }
    if (n.client_id && (n.type === "portal_login" || (n.url && n.url.startsWith("/clienti")))) {
      return `/clienti/${n.client_id}`;
    }
    return n.url || "/notifiche";
  };

  const openItem = async (n) => {
    setOpen(false);
    // ottimistico: marca letto in UI
    if (!n.read) {
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { console.debug("read fail", e); }
    }
    navigate(destFor(n));
  };

  const markAll = async () => {
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch (e) { console.debug("read-all fail", e); }
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost" size="icon" className="relative"
          data-testid="notification-bell-button" aria-label="Notifiche"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--hi-status-error,0_72%_51%))] text-white text-[10px] font-semibold flex items-center justify-center leading-none border border-background"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end" className="w-[360px] p-0 overflow-hidden"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Notifiche</span>
            {unread > 0 && (
              <span className="text-[11px] text-muted-foreground">{unread} non lette</span>
            )}
          </div>
          {unread > 0 && (
            <button
              type="button" onClick={markAll}
              data-testid="notification-mark-all"
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Segna tutte
            </button>
          )}
        </div>

        <ScrollArea className="max-h-[380px]">
          {loading && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notification-loading">
              Caricamento…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-4 py-10 text-center" data-testid="notification-empty">
              <Bell className="h-7 w-7 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">Nessuna notifica</p>
              <p className="text-[11px] text-muted-foreground/70 mt-1">
                Qui vedrai lista d'attesa, accessi e prenotazioni online.
              </p>
            </div>
          )}

          {!loading && items.map((n) => {
            const Icon = typeIcon(n.type);
            return (
              <button
                key={n.id} type="button" onClick={() => openItem(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border/60 transition-colors hover:bg-accent ${
                  n.read ? "" : "bg-[hsl(var(--hi-gold-soft))]"
                }`}
              >
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  n.read ? "bg-muted text-muted-foreground" : "bg-[hsl(var(--hi-gold)/0.18)] text-[hsl(var(--hi-gold))]"
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm truncate ${n.read ? "font-medium" : "font-semibold"}`}>
                      {n.title}
                    </p>
                    {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--hi-gold))]" />}
                  </div>
                  {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                  <p className="text-[11px] text-muted-foreground/70 mt-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {relTime(n.created_at)}
                  </p>
                </div>
              </button>
            );
          })}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
